import { isAvailableBecauseCanEditFile, isAvailableBecauseLoggedIn } from '@/app/actions';
import { Action } from '@/app/actions/actions';
import type { ActionAvailabilityArgs, ActionSpecRecord } from '@/app/actions/actionsSpec';
import { sheets } from '@/app/grid/controller/Sheets';
import { getAllSelection } from '@/app/grid/sheet/selection';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { downloadCsvFile, downloadExcelFile, downloadQuadraticFile } from '@/app/helpers/downloadFileInBrowser';
import { isEmbed } from '@/app/helpers/isEmbed';
import { quadraticCore } from '@/app/web-workers/quadraticCore/quadraticCore';
import {
  CsvIcon,
  DownloadExcelIcon,
  DownloadIcon,
  ExportIcon,
  FileRenameIcon,
  HistoryIcon,
  PersonAddIcon,
} from '@/shared/components/Icons';
import { trackEvent } from '@/shared/utils/analyticsEvents';

type FileActionSpec = Pick<
  ActionSpecRecord,
  | Action.FileShare
  | Action.FileExport
  | Action.FileRename
  | Action.FileDownload
  | Action.FileVersionHistory
  | Action.FileDownloadExcel
  | Action.FileDownloadExcelSubmenu
  | Action.FileDownloadCsv
>;

export type FileActionArgs = {
  [Action.FileDownload]: { name: string; uuid: string };
  [Action.FileVersionHistory]: { uuid: string };
  [Action.FileDownloadExcel]: { name: string; uuid: string };
  [Action.FileDownloadExcelSubmenu]: { name: string; uuid: string };
  [Action.FileDownloadCsv]: { name: string; uuid: string };
};

export const fileActionsSpec: FileActionSpec = {
  [Action.FileShare]: {
    label: () => 'Share',
    Icon: PersonAddIcon,
    isAvailable: ({ isAuthenticated, isVersionHistoryPreview }: ActionAvailabilityArgs) =>
      !isEmbed && isAuthenticated && !isVersionHistoryPreview,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({ ...prev, showShareFileMenu: true }));
    },
  },
  [Action.FileExport]: {
    label: () => 'Export',
    Icon: ExportIcon,
    isAvailable: ({ isAuthenticated, isVersionHistoryPreview }: ActionAvailabilityArgs) =>
      isAuthenticated && !isVersionHistoryPreview,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({ ...prev, showExportFileMenu: true }));
    },
  },
  [Action.FileRename]: {
    label: () => 'Rename',
    Icon: FileRenameIcon,
    isAvailable: isAvailableBecauseCanEditFile,
    run: () => {
      gridSettings.updateEditorInteractionState((prev) => ({ ...prev, showRenameFileMenu: true }));
    },
  },
  [Action.FileDownload]: {
    label: () => 'Quadratic (.grid)',
    labelVerbose: 'Download as Quadratic (.grid)',
    Icon: DownloadIcon,
    isAvailable: isAvailableBecauseLoggedIn,
    run: async ({ name, uuid }: FileActionArgs[Action.FileDownload]) => {
      trackEvent('[Files].downloadFile', { id: uuid });
      gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: true }));
      const data = await quadraticCore.export();
      downloadQuadraticFile(name, data);
      gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: false }));
    },
  },
  [Action.FileDownloadExcel]: {
    label: () => 'Download as Excel',
    labelVerbose: 'Download as Excel (.xlsx)',
    Icon: DownloadExcelIcon,
    isAvailable: isAvailableBecauseCanEditFile,
    run: async ({ name, uuid }: FileActionArgs[Action.FileDownloadExcel]) => {
      try {
        trackEvent('[Files].exportExcel', { id: uuid });
        gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: true }));
        const data = await quadraticCore.exportExcel();
        downloadExcelFile(name, data);
        gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: false }));
      } catch (e) {
        gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: false }));
        gridSettings.addGlobalSnackbar?.('Failed to export Excel', {
          severity: 'error',
        });
        console.error(e);
      }
    },
  },
  [Action.FileDownloadExcelSubmenu]: {
    label: () => 'Excel (.xlsx)',
    Icon: DownloadExcelIcon,
    isAvailable: isAvailableBecauseCanEditFile,
    run: async ({ name, uuid }: FileActionArgs[Action.FileDownloadExcelSubmenu]) => {
      try {
        trackEvent('[Files].exportExcel', { id: uuid });
        gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: true }));
        const data = await quadraticCore.exportExcel();
        downloadExcelFile(name, data);
        gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: false }));
      } catch (e) {
        gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: false }));
        gridSettings.addGlobalSnackbar?.('Failed to export Excel', {
          severity: 'error',
        });
        console.error(e);
      }
    },
  },
  [Action.FileDownloadCsv]: {
    label: () => 'CSV (current sheet only)',
    labelVerbose: 'Download as CSV (current sheet only)',
    Icon: CsvIcon,
    isAvailable: (args) => !isEmbed && isAvailableBecauseCanEditFile(args),
    run: async ({ name, uuid }: FileActionArgs[Action.FileDownloadCsv]) => {
      try {
        trackEvent('[Files].exportCsv', { id: uuid });
        gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: true }));
        const filename = `${name} - ${sheets.sheet.name}`;
        const sheetBounds = sheets.sheet.boundsWithoutFormatting;

        if (sheetBounds.type === 'empty') {
          downloadCsvFile(filename, new Uint8Array());
        } else {
          const selection = getAllSelection(sheets.sheet.id);
          const data = await quadraticCore.exportCsvSelection(selection);
          downloadCsvFile(filename, new TextEncoder().encode(data) as Uint8Array);
        }

        gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: false }));
      } catch (e) {
        gridSettings.updateEditorInteractionState((prev) => ({ ...prev, isRunningAsyncAction: false }));
        gridSettings.addGlobalSnackbar?.('Failed to export CSV', {
          severity: 'error',
        });
        console.error(e);
      }
    },
  },
  [Action.FileVersionHistory]: {
    label: () => 'Version history',
    Icon: HistoryIcon,
    isAvailable: ({ isAuthenticated, filePermissions, teamPermissions }: ActionAvailabilityArgs) =>
      Boolean(isAuthenticated && filePermissions.includes('FILE_EDIT') && teamPermissions?.includes('TEAM_VIEW')),
    run: ({ uuid }: { uuid: string }) => {
      import('@/app/atoms/fileHistoryAtom').then(({ showFileHistoryPanel }) => {
        showFileHistoryPanel(uuid);
      });
    },
  },
};
